import { RootState } from "@/redux/store";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import { useGetAllMemberQuery, } from "@/services/memberApi";
import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSelector } from "react-redux";
import { CalendarIcon, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetStaffListQuery } from "@/services/staffsApi";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "lucide-react";
import { FormControl, FormItem } from "@/components/ui/form";
import { InvoicePayload } from "@/types/invoice";
import { AutoComplete, Option } from "@/components/ui/autocomplete";

interface InvoiceHeaderProps {
  control: Control<InvoicePayload>;
  errors: FieldErrors<InvoicePayload>;
  watch: UseFormWatch<InvoicePayload>;
  setValue: UseFormSetValue<InvoicePayload>;
  action: string;
}
const InvoiceHeader = ({
  control,
  errors,
  watch,
  setValue,
  action,
}: InvoiceHeaderProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const watcher = watch()
  const { data: staffList } = useGetStaffListQuery(orgId);
  const [openStaffDropdown, setStaffDropdown] = React.useState(false);

  const {
    data: users,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetAllMemberQuery(
    { org_id: orgId, query: `` },
  );


  const handleUserSelect = (selectedUser: any) => {
    console.log({ selectedUser })
    setValue("member_id", selectedUser.id);
    setValue(
      "member_name",
      selectedUser.first_name + " " + selectedUser.last_name
    );
    setValue("member_email", selectedUser.email);
    setValue("member_address", selectedUser.address_1);
    setValue("member_nic", selectedUser.nic);
    setValue("member_gender", selectedUser.gender);
    setValue("is_business", selectedUser.is_business);
  };

  return (
    <div className=" rounded-2xl bg-white">
      <CustomCollapsible
        hideToggle={false}
        isOpenDefault={true}
        title="Invoice Detail"
      >
        <div className="flex flex-row gap-5 justify-start items-center">
          <div className="w-[30%]">
            <p className="text-base font-normal">
              Bill To <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="member_name"
              rules={{
                required: "Required",
              }}
              render={({ field: { onChange, value, ref } }) => (
                <AutoComplete
                  options={
                    (users &&
                      users?.data.map((data) => ({
                        ...data,
                        own_id: data.own_member_id,
                        value: "" + data.id,
                        label: data.first_name + " " + data.last_name,
                      }))) ||
                    []
                  }
                  onValueChange={(selectedUser) => {
                    onChange(selectedUser); // Set the selected user in the form state
                    handleUserSelect(selectedUser); // Call additional logic if needed
                  }}
                  value={action == "edit" || action == "member-created" ? {
                    value: watcher.id?.toString(),
                    label: watcher.member_name,
                  } as Option : undefined}
                  placeholder="Search for a Member"
                  emptyMessage="No member found"
                />
              )}
            />
            {errors.member_name?.message && (
              <p className="text-red-500 text-xs mt-[5px]">
                {errors.member_name.message.toString()}
              </p>
            )}
          </div>

          <div className="w-[30%]">
            <p className="text-base font-normal">
              Invoice Number <span className="text-red-500">*</span>
            </p>{" "}
            <Controller
              name="invoice_number"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <div>
                  <Input
                    {...field}
                    id="invoice_number"
                    placeholder="Enter invoice number"
                    disabled
                  />
                  {fieldState.error && (
                    <p className="text-red-500 text-sm">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            // rules={{ required: "Required" }}
            />
          </div>
          <div className="w-[30%]">
            <p className="text-base font-normal">
              Processed By <span className="text-red-500">*</span>
            </p>
            <div className="w-full">
              <Popover open={openStaffDropdown} onOpenChange={setStaffDropdown}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="font-normal capitalize w-full hover:bg-transparent border-[1px] shadow-sm bg-white"
                    aria-expanded={openStaffDropdown}
                  >
                    <span className="w-full text-left font-normal">
                      {watcher.seller_id
                        ? staffList?.find((staff) => staff.value == watcher.seller_id)
                          ?.label
                        : "Select Staff"}
                    </span>
                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 relative">
                  <Command className="w-full">
                    <CommandList>
                      <CommandInput
                        placeholder="Select Staff"
                        className="font-normal"
                      />
                      <CommandEmpty>No Staff matched the Search.</CommandEmpty>
                      <CommandGroup className="capitalize">
                        {staffList &&
                          staffList.map((staff) => (
                            <CommandItem
                              value={staff.label}
                              key={staff.value}
                              className="font-normal"
                              onSelect={() => {
                                setValue("seller_id", Number(staff.value));
                                setValue("seller_name", staff.label);
                                setStaffDropdown(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                  staff.value == watcher.seller_id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {staff.label}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-5 justify-start items-center mt-4">
          <div className="w-[20%]">
            <p className="text-base font-normal">
              Invoice Date<span className="text-red-500">*</span>
            </p>
            <Controller
              name="payment_date"
              control={control}
              defaultValue=""
              disabled={true} // Disable the input field
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="date"
                        {...field} // Spread the field props to connect the input with the controller
                        value={
                          field.value
                            ? new Date(field.value).toISOString().split("T")[0]
                            : ""
                        } // Format the date to YYYY-MM-DD
                        disabled={true} // Disable the input field
                        className="w-full pl-3 text-center font-normal"
                      />
                    </div>
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-red-500 text-sm">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
              rules={{ required: "Required" }}
            />
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default InvoiceHeader;
